/**
 * The base of the component set (§12.4). Hand-built, deliberately: the total
 * surface is a dozen components, and a library's opinions about radius,
 * shadow and spacing are exactly what produces the look this is avoiding.
 *
 * Two radii, borders instead of shadows, and colour only where it carries
 * meaning.
 */
import {
  forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode,
} from 'react'
import { cn } from '~/lib/cn.ts'

// ---------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md'

/**
 * `relative tap` is what makes every button in the app touch-safe without a
 * single call site changing: `.tap` grows an invisible hit rectangle of
 * `--tap` around the painted box, and `--tap` is 0 on a mouse. The button
 * keeps the density it was designed with and gains a finger-sized target
 * only where one is needed (§12.4).
 */
const BUTTON_BASE =
  'relative tap inline-flex items-center justify-center gap-1.5 whitespace-nowrap ' +
  'rounded-[var(--radius-control)] border font-medium transition-colors duration-75 ' +
  'disabled:pointer-events-none disabled:opacity-45 ' +
  // A press has to be visible on a touchscreen, where there is no hover to
  // tell you the control noticed you.
  'active:scale-[0.97] motion-reduce:active:scale-100'

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  default:
    'border-[var(--border)] bg-[var(--bg-raised)] text-[var(--text)] ' +
    'hover:bg-[var(--bg-sunken)] hover:border-[var(--border-strong)] active:bg-[var(--bg-sunken)]',
  primary:
    'border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-fg)] ' +
    'hover:brightness-110 active:brightness-95',
  ghost:
    'border-transparent bg-transparent text-[var(--text-muted)] ' +
    'hover:bg-[var(--bg-sunken)] hover:text-[var(--text)]',
  danger:
    'border-[var(--border)] bg-[var(--bg-raised)] text-[var(--fail)] ' +
    'hover:bg-[var(--fail-bg)] hover:border-[var(--fail)]',
}

// Heights come from tokens, not utilities, so the coarse-pointer and
// density tiers in tokens.css retune the whole control set at once.
const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: 'h-[var(--control-h-sm)] px-2.5 text-[length:var(--text-tiny)]',
  md: 'h-[var(--control-h-md)] px-3.5 text-[length:var(--text-data)]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], className)}
      {...rest}
    />
  )
})

/**
 * A square button holding one glyph. `label` is required and becomes the
 * accessible name -- an icon button without one is a button nobody using a
 * screen reader can press.
 */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  variant?: ButtonVariant
  size?: ButtonSize
  active?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ label, variant = 'ghost', size = 'md', active, className, type = 'button', ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={label}
        aria-pressed={active === undefined ? undefined : active}
        className={cn(
          BUTTON_BASE,
          BUTTON_VARIANT[variant],
          size === 'sm'
            ? 'size-[var(--control-h-sm)]'
            : 'size-[var(--control-h-md)]',
          'px-0',
          active && 'bg-[var(--accent-soft)] text-[var(--accent)] border-[var(--accent)]',
          className,
        )}
        {...rest}
      />
    )
  },
)

// ---------------------------------------------------------------------
// Chip — the filter's workhorse
// ---------------------------------------------------------------------

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  selected?: boolean
  /** Greyed and non-interactive, but still present. */
  unavailable?: boolean
  count?: number | null
  children: ReactNode
}

/**
 * An option that no longer matches stays in the list, greyed, with its count
 * at zero (§7.1). Options that appear and vanish as you type make a filter
 * feel broken; options that grey out explain themselves.
 */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { selected, unavailable, count, children, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      role="checkbox"
      aria-checked={!!selected}
      disabled={unavailable && !selected}
      className={cn(
        // `items-center` on the box with `items-baseline` on the inner row:
        // the chip grows to a tappable height without the count drifting off
        // the label's baseline.
        'group relative tap inline-flex items-center rounded-[var(--radius-control)] border px-2 py-[3px]',
        'min-h-[var(--control-h-sm)]',
        'text-[length:var(--text-data)] leading-[var(--text-data--line-height)] transition-colors duration-75',
        'active:scale-[0.97] motion-reduce:active:scale-100',
        'disabled:cursor-default disabled:active:scale-100',
        selected
          ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)] font-medium'
          : unavailable
            ? 'border-[var(--border)] bg-transparent text-[var(--text-faint)]'
            : 'border-[var(--border)] bg-[var(--bg-raised)] text-[var(--text)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-sunken)]',
        className,
      )}
      {...rest}
    >
      <span className="inline-flex items-baseline gap-1.5">
        <span>{children}</span>
        {count !== null && count !== undefined && (
          <span
            className={cn(
              'tnum text-[length:var(--text-micro)]',
              selected ? 'text-[var(--accent)] opacity-80' : 'text-[var(--text-faint)]',
            )}
          >
            {count}
          </span>
        )}
      </span>
    </button>
  )
})

// ---------------------------------------------------------------------
// State: badge, dot, bar
// ---------------------------------------------------------------------

export type State = 'pass' | 'warn' | 'fail' | 'neutral' | 'accent'

const STATE_CHIP: Record<State, string> = {
  pass: 'bg-[var(--pass-bg)] text-[var(--pass)] border-[var(--pass)]/30',
  warn: 'bg-[var(--warn-bg)] text-[var(--warn)] border-[var(--warn)]/30',
  fail: 'bg-[var(--fail-bg)] text-[var(--fail)] border-[var(--fail)]/30',
  neutral: 'bg-[var(--bg-sunken)] text-[var(--text-muted)] border-[var(--border)]',
  accent: 'bg-[var(--accent-soft)] text-[var(--accent)] border-[var(--accent)]/30',
}

const STATE_DOT: Record<State, string> = {
  pass: 'bg-[var(--pass)]',
  warn: 'bg-[var(--warn)]',
  fail: 'bg-[var(--fail)]',
  neutral: 'bg-[var(--text-faint)]',
  accent: 'bg-[var(--accent)]',
}

export function Badge({
  state = 'neutral', className, children, ...rest
}: HTMLAttributes<HTMLSpanElement> & { state?: State }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-[var(--radius-control)] border',
        'px-1.5 py-px text-[length:var(--text-micro)] font-medium uppercase tracking-[0.03em]',
        STATE_CHIP[state],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  )
}

/**
 * The quality dot used in the page rail and the coverage matrix. Grey is
 * deliberately visible rather than absent: a page with nothing extracted is
 * a real finding, not a blank.
 */
export function Dot({
  state = 'neutral', size = 6, hollow, className, title,
}: { state?: State; size?: number; hollow?: boolean; className?: string; title?: string }) {
  return (
    <span
      title={title}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
      className={cn(
        'inline-block shrink-0 rounded-full',
        hollow ? 'border' : STATE_DOT[state],
        hollow && 'border-[var(--border-strong)]',
        className,
      )}
      style={{ width: size, height: size }}
    />
  )
}

/**
 * A pass-rate bar. Ten cells rather than a continuous fill, because a
 * discrete bar is read as a measurement and a smooth one as decoration.
 */
export function Bar({
  value, cells = 10, className, state,
}: { value: number | null; cells?: number; className?: string; state?: State }) {
  const filled = value === null ? 0 : Math.round(value * cells)
  const s: State = state ?? (value === null ? 'neutral' : value >= 0.9 ? 'pass' : value >= 0.75 ? 'warn' : 'fail')
  return (
    <span className={cn('inline-flex gap-px align-middle', className)} aria-hidden="true">
      {Array.from({ length: cells }, (_, i) => (
        <span
          key={i}
          className={cn(
            'h-2.5 w-[3px] rounded-[1px]',
            i < filled ? STATE_DOT[s] : 'bg-[var(--border)]',
          )}
        />
      ))}
    </span>
  )
}

/** Pass rate to a band, using the same thresholds the API facets use. */
export function bandOf(rate: number | null | undefined): State {
  if (rate === null || rate === undefined) return 'neutral'
  return rate >= 0.9 ? 'pass' : rate >= 0.75 ? 'warn' : 'fail'
}

// ---------------------------------------------------------------------
// Layout atoms
// ---------------------------------------------------------------------

/** A small-caps section label. Used everywhere a group needs a name. */
export function Label({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'text-[length:var(--text-micro)] font-semibold uppercase tracking-[0.06em] text-[var(--text-faint)]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export function Panel({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-container)] border border-[var(--border)] bg-[var(--bg-raised)]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export function Skeleton({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse rounded-[var(--radius-control)] bg-[var(--bg-sunken)]',
        className,
      )}
      {...rest}
    />
  )
}

/** A definition pair, the app's most repeated shape. */
export function Field({
  label, children, className, mono,
}: { label: ReactNode; children: ReactNode; className?: string; mono?: boolean }) {
  return (
    <div className={cn('min-w-0', className)}>
      <Label>{label}</Label>
      <div
        className={cn(
          'mt-0.5 text-[length:var(--text-data)] text-[var(--text)]',
          mono && 'font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)]',
        )}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * A value the database does not have. Never "N/A", never zero -- a zero
 * could be a real reading, and the difference matters here (§7).
 */
export function NotRecorded({ children }: { children?: ReactNode }) {
  return (
    <span className="text-[length:var(--text-tiny)] italic text-[var(--text-faint)]">
      {children ?? 'not recorded'}
    </span>
  )
}
